import { useState, useEffect, useCallback, useMemo } from "react";
import {
  getBugsByProject,
  addBug,
  updateBugStatus,
  deleteBug,
  addVersion,
  getProjectUsers,
  inviteUserToProject,
  leaveProject,
  deleteProject,
  getVersions,
  renameVersion as apiRename,
  updateVersion as apiUpdateVersion,
} from "../services/BugService";
import Cookies from "js-cookie";
import {
  getProjectMembers,
  removeMemberFromProject,
  updateProjectMemberRole,
} from "../services/ProjectService";
import { getUserInfo } from "../services/UserService";
import { post } from "../services/ApiService";

const DEFAULT_VERSION = "v1.0";

const toBool = (value) =>
  value === true ||
  value === 1 ||
  value === "1" ||
  value === "true" ||
  value === "yes";

const normalizeId = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).trim();
};

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const getMemberKey = (member = {}, index = 0) => {
  const id = normalizeId(
    member.user_id ??
      member.id ??
      member.member_id ??
      member.project_user_id ??
      member.userId ??
      member.uid
  );
  const email = normalizeEmail(member.email ?? member.mail);
  const name = String(member.name ?? member.full_name ?? member.username ?? "").trim();

  if (id) return `id:${id}`;
  if (email) return `mail:${email}`;
  if (name) return `name:${name}:${index}`;
  return `idx:${index}`;
};

const getMemberRole = (member = {}) => {
  const rawRole = String(
    member.role ??
      member.member_role ??
      member.project_role ??
      member.permission ??
      member.user_role ??
      member.type ??
      ""
  )
    .trim()
    .toLowerCase();

  if (
    toBool(member.is_owner) ||
    toBool(member.owner) ||
    toBool(member.is_project_owner) ||
    rawRole === "owner" ||
    rawRole === "sahip"
  ) {
    return "owner";
  }

  if (
    toBool(member.is_admin) ||
    toBool(member.admin) ||
    toBool(member.is_manager) ||
    toBool(member.manager) ||
    rawRole === "admin" ||
    rawRole === "administrator" ||
    rawRole === "manager" ||
    rawRole === "yönetici" ||
    rawRole === "yonetici"
  ) {
    return "manager";
  }

  return "member";
};

const normalizeMember = (member = {}) => {
  const id = normalizeId(
    member.user_id ??
      member.id ??
      member.member_id ??
      member.project_user_id ??
      member.userId ??
      member.uid
  );
  const email = String(member.email ?? member.mail ?? "").trim();
  const name = String(
    member.name ??
      member.full_name ??
      member.display_name ??
      member.username ??
      email ??
      (id ? `Kullanıcı ${id}` : "Kullanıcı")
  ).trim();
  const role = getMemberRole(member);

  return {
    ...member,
    id,
    email,
    name,
    role,
    isOwner: role === "owner",
    isManager: role === "owner" || role === "manager",
  };
};

const mergeMembers = (...lists) => {
  const map = new Map();

  lists
    .flat()
    .filter(Boolean)
    .forEach((member, index) => {
      const key = getMemberKey(member, index);
      const prev = map.get(key) || {};
      map.set(key, { ...prev, ...member });
    });

  return Array.from(map.values())
    .map(normalizeMember)
    .sort((a, b) => {
      const rank = { owner: 0, manager: 1, member: 2 };
      const roleDiff = (rank[a.role] ?? 9) - (rank[b.role] ?? 9);
      if (roleDiff !== 0) return roleDiff;
      return String(a.name || "").localeCompare(String(b.name || ""), "tr");
    });
};

const readStoredUserId = () =>
  normalizeId(
    localStorage.getItem("user_id") ||
      localStorage.getItem("userId") ||
      localStorage.getItem("uid") ||
      Cookies.get("user_id") ||
      Cookies.get("uid") ||
      ""
  );

const readStoredUserEmail = () => {
  try {
    return normalizeEmail(localStorage.getItem("user_email") || Cookies.get("user_email") || "");
  } catch {
    return "";
  }
};

const useBugs = (projectId, projectName = "") => {
  const [bugs, setBugs] = useState({});
  const [users, setUsers] = useState([]);
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const token = Cookies.get("token");

  const group = (list) =>
    list.reduce((a, b) => {
      const v = b.version_name;
      a[v] = a[v] ? [...a[v], b] : [b];
      return a;
    }, {});

  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const bugList = await getBugsByProject(projectId);
      const grouped = group(bugList);
      setBugs(grouped);

      const basicUsers = await getProjectUsers(projectId).catch(() => []);
      const memberUsers = await getProjectMembers(projectId).catch(() => []);
      setUsers(mergeMembers(basicUsers, memberUsers));

      const v = await getVersions(projectId);
      const arr = v.versions || v.data?.versions || [];
      setVersions(
        arr.length
          ? arr
          : Object.keys(grouped).map((n) => ({ id: n, version_name: n }))
      );
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    let alive = true;

    const fetchCurrentUser = async () => {
      try {
        const user = await getUserInfo();
        if (alive) {
          setCurrentUser(user || null);
        }
      } catch {
        if (alive) {
          setCurrentUser(null);
        }
      }
    };

    fetchCurrentUser();

    return () => {
      alive = false;
    };
  }, []);

  const currentUserId = useMemo(
    () => normalizeId(currentUser?.id ?? currentUser?.user_id ?? readStoredUserId()),
    [currentUser]
  );

  const currentUserEmail = useMemo(
    () => normalizeEmail(currentUser?.email) || readStoredUserEmail(),
    [currentUser]
  );

  const currentMember = useMemo(
    () =>
      users.find((member) => {
        const memberId = normalizeId(member.id ?? member.user_id);
        const memberEmail = normalizeEmail(member.email);
        if (currentUserId && memberId && memberId === currentUserId) return true;
        if (currentUserEmail && memberEmail && memberEmail === currentUserEmail) return true;
        return false;
      }) || null,
    [users, currentUserId, currentUserEmail]
  );

  const canManageMembers = !!currentMember?.isManager;

  const createBug = async (data) => {
    const response = await addBug({
      ...data,
      project_id: projectId,
      token,
      version_name: data.version_name || DEFAULT_VERSION,
    });

    // Yeni bug eklendiğinde proje üyelerine bildirim gönder
    const bugId = response?.bug?.id ?? response?.id ?? response?.bug_id ?? response?.data?.id ?? "";
    const actorId = currentUserId || readStoredUserId();
    const bugTitle = String(data.title || data.bug_title || "Yeni İş").trim();
    const decodedProjectName = projectName
      ? decodeURIComponent(projectName)
      : "";

    if (actorId && projectId) {
      const href = `/bugs/${encodeURIComponent(projectId)}/1/${encodeURIComponent(decodedProjectName)}${bugId ? `?bug=${encodeURIComponent(String(bugId))}` : ""}`;

      // Tüm proje üyelerine backend üzerinden bildirim broadcast et (fire & forget)
      post("/notifications.php", {
        title: `Yeni İş: ${bugTitle}`,
        project_id: projectId,
        project_name: decodedProjectName,
        actor_user_id: Number(actorId) || 0,
        bug_id: bugId ? Number(bugId) : 0,
        href,
      }).catch(() => {});
    }

    // DOM event — aynı sekmedeki Header'ın "Son Buglar" listesini anında günceller
    window.dispatchEvent(
      new CustomEvent("bug:created", {
        detail: {
          id: String(bugId),
          bug_id: String(bugId),
          title: bugTitle,
          projectName: projectName ? decodeURIComponent(projectName) : "",
          projectUniq: projectId,
          projectId,
          created_by: actorId,
        },
      })
    );

    await refetch();

    // Çağıran taraf (etiket bağlama vb.) yeni bug id'sine erişebilsin diye response döndürülür
    return response;
  };

  const toggleBug = async (id, done) => {
    setBugs((prev) => {
      const copy = { ...prev };
      Object.keys(copy).forEach(
        (v) =>
          (copy[v] = copy[v].map((b) =>
            b.id === id ? { ...b, is_completed: !done } : b
          ))
      );
      return copy;
    });
    try {
      await updateBugStatus(id, !done, 16);
    } catch {
      refetch();
    }
  };

  const removeBug = async (id) => {
    setBugs((p) => {
      const c = { ...p };
      Object.keys(c).forEach((v) => (c[v] = c[v].filter((b) => b.id !== id)));
      return c;
    });
    try {
      await deleteBug(id, 1, token);
    } catch {
      await refetch();
    }
  };

  const createVersion = async (name) => {
    await addVersion(projectId, name);
    await refetch();
  };

  const renameVersion = async (id, name) => {
    await apiRename(id, name);
    await refetch();
  };

  const addUser = async (email) => {
    const response = await inviteUserToProject(projectId, email);
    await refetch();
    return response;
  };

  const updateVersion = async (payload) => {
    await apiUpdateVersion(payload);
    await refetch();
  };

  const removeUser = async (userId) => {
    await removeMemberFromProject(projectId, userId);
    await refetch();
  };

  const changeUserRole = async (userId, role) => {
    await updateProjectMemberRole(projectId, userId, role);
    await refetch();
  };

  const leave = () => leaveProject(projectId, currentUserId || "1", token);
  const removeProject = () => deleteProject(projectId, currentUserId || "1", token);

  return {
    bugs,
    versions,
    users,
    loading,
    createBug,
    toggleBug,
    removeBug,
    createVersion,
    renameVersion,
    addUser,
    removeUser,
    changeUserRole,
    leave,
    removeProject,
    updateVersion,
    currentUser,
    currentUserId,
    currentUserEmail,
    canManageMembers,
  };
};
export default useBugs;
